import FalconComponentCard from "components/common/FalconComponentCard";
import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, Form } from "react-bootstrap";
import { FiDownloadCloud } from "react-icons/fi";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import EligibilityCheckListFilter from "./components/EligibilityCheckListFilter";
import EligibilityCheckListTable from "./components/EligibilityCheckListTable";
import {
  GetEligibilityCheckList,
  GetSingleEligibilityData,
} from "./config/eligibilityCheckListApi";
import { getToday } from "helpers/utils";
import { handleResetAll } from "features/preauthorization/config/preauthorizationSlice";
import { handleResetPatientSearch } from "../eliglibility-check/config/eligibilityCheckSlice";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { handleDownloadXLSX } from "helpers/excel";
import { handleResetFilter } from "./config/eligibilityCheckListSlice";
import {
  handlePreauthFormData,
  handleResetClaimAll,
  handleResetCreateClaim,
} from "features/cliams/config/claimSlice";

const EligibilityCheckList = () => {
  const dispatch = useDispatch();
  const { eligibilityCheckList, eligibilityFilters } = useSelector(
    (state) => state.eligibilityCheckList,
  );
  const { allPermissionType } = useSelector((state) => state.role);
  const eligibilitySetting = allPermissionType?.[0]?.rolemngmt_name?.find(
    (item) => item?.rolemngmttyp_name === "Eligibility",
  );
  const selectedBranch = JSON.parse(localStorage.getItem("selectedBranch"));

  const { control, watch, handleSubmit, setValue, reset } = useForm({
    defaultValues: {
      poyer: eligibilityFilters?.payer,
      insurance: eligibilityFilters?.insurance_company,
      start_date: eligibilityFilters?.start_date,
      end_date: eligibilityFilters?.end_date,
      patient_id: "",
      pat_det: "",
      patient_mrn: "",
      status: eligibilityFilters?.status,
      identifier_type: eligibilityFilters?.identifier_type,
    },
  });

  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [resetTrigger, setResetTrigger] = useState(0);

  // Reset redux and search state on mount
  useEffect(() => {
    dispatch(handleResetAll({ check: "reset" }));
    dispatch(handleResetCreateClaim());
    dispatch(handlePreauthFormData({ check: "reset" }));
    dispatch(handleResetPatientSearch());
  }, []);

  // Re-fetch data on pagination change
  useEffect(() => {
    fetchEligibilityList(buildRequestBody(page, rowsPerPage));
  }, [page, rowsPerPage]);

  const buildRequestBody = (customPage = page, limit = rowsPerPage) => ({
    branch_id:
      eligibilityFilters?.branch?.branch_id ||
      selectedBranch?.value ||
      selectedBranch?.branch_id ||
      1,
    payer_id: eligibilityFilters?.payer?.payer_id || "",
    insurance_company_id:
      eligibilityFilters?.insurance_company?.insurance_company_id || "",
    frm_dte: moment(eligibilityFilters?.start_date).isValid()
      ? moment(eligibilityFilters?.start_date).format("DD-MM-YYYY")
      : "",
    to_dte: moment(eligibilityFilters?.end_date).isValid()
      ? moment(eligibilityFilters?.end_date).format("DD-MM-YYYY")
      : "",
    pat_det: eligibilityFilters?.patient || "",
    elg_sts: eligibilityFilters?.status?.allstatus_code || "",
    identifier_type: eligibilityFilters?.identifier_type?.ident_type_id || "",
    start_point: customPage,
    limit: limit,
  });

  const fetchEligibilityList = async (body = {}) => {
    try {
      const response = await dispatch(GetEligibilityCheckList(body)).unwrap();
      setTotalRows(response?.total_rows || 0);
    } catch (error) {
      console.error("Eligibility list fetch failed:", error);
    }
  };

  const handleCheckListFilter = () => {
    const newPage = 0;
    setPage(newPage); // Reset pagination to first page
    const requestBody = buildRequestBody(newPage, rowsPerPage);
    fetchEligibilityList(requestBody);
  };

  const handleDownloadExcel = async () => {
    const body = {
      branch_id:
        eligibilityFilters?.branch?.branch_id ||
        selectedBranch?.value ||
        selectedBranch?.branch_id ||
        1,
      payer_id: eligibilityFilters?.payer?.payer_id || "",
      insurance_company_id:
        eligibilityFilters?.insurance_company?.insurance_company_id || "",
      frm_dte: moment(eligibilityFilters?.start_date).isValid()
        ? moment(eligibilityFilters?.start_date).format("DD-MM-YYYY")
        : "",
      to_dte: moment(eligibilityFilters?.end_date).isValid()
        ? moment(eligibilityFilters?.end_date).format("DD-MM-YYYY")
        : "",
      pat_det: eligibilityFilters?.patient || "",
      elg_sts: eligibilityFilters?.status?.allstatus_code || "",
      identifier_type: eligibilityFilters?.identifier_type?.ident_type_id || "",
      start_point: page,
      limit: totalRows,
    };

    const response = await dispatch(GetEligibilityCheckList(body)).unwrap();
    const data = response?.data?.eligibilitycheck_data || [];

    const headers = [
      "#",
      "Branch",
      "Eligibility Id",
      "Bundle Request Id",
      "Date",
      "Insurance",
      "Mrn",
      "Iqama/National Id",
      "Patient Name",
      "Policy Class",
      "Policy Name",
      "Policy Number",
      "Membership No.",
      "Eligibility Status",
      "Remarks",
      "Gender",
      "Marital Status",
      "Occupation",
      "Relationship",
      "Created By",
    ];

    const rows = [];

    for (let index = 0; index < data.length; index++) {
      const row = data[index];
      // Build Excel row with extracted data
      rows.push([
        index + 1,
        selectedBranch?.branch_name || "",
        row?.eligibility_checks_id || "",
        row?.req_bundle_id || "",
        row?.created_at
          ? moment(row.created_at).format("DD-MM-YYYY , hh:mm:ss")
          : "",
        row?.insurance_direct === 1
          ? `${row?.payer_name}`
          : `${row?.payer_name} - ${row?.insurance_shname}`,
        row?.patient_mrn || "",
        row?.eligibility_checks_patientiqama || "",
        `${row?.eligibility_checks_patientfirstname || ""} ${
          row?.eligibility_checks_patientfamilyname || ""
        }`,
        row?.class_name || "",
        row?.policy_holder || "",
        row?.policy_number || "",
        row?.memberid || "", // Now populated
        row?.eligibility_checks_status || "",
        row?.remarks || "",
        row?.patient_gender || "",
        row?.patient_maritalstatus || "",
        row?.patient_occupation || "",
        row?.relation || "",
        `${row?.employees_firstname || ""} ${row?.employees_familyname || ""}`,
      ]);
    }

    // Rest of Excel generation code remains the same
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Eligibility Check");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(
      blob,
      `Eligibility_Report_${moment().format("DD-MM-YYYY , hh:mm:ss")}.xlsx`,
    );
  };

  console.log({ eligibilityCheckList });

  const handleReset = async () => {
    const defaultBranch = selectedBranch
      ? {
          branch_name: selectedBranch.label || selectedBranch.branch_name,
          branch_id: selectedBranch.value || selectedBranch.branch_id,
          branch_cchino: selectedBranch.cchi || selectedBranch.branch_cchino,
        }
      : null;

    reset({
      branch: defaultBranch,
      poyer: "",
      insurance: "",
      start_date: getToday(),
      end_date: getToday(),
      patient_id: "",
      pat_det: "",
      patient_mrn: "",
      identifier_type: "",
      status: "",
    });

    setPage(0);
    setRowsPerPage(10);
    setResetTrigger(Math.random());
    dispatch(handleResetFilter());
    if (defaultBranch) {
      dispatch(
        handleElibilityFilter({ check: "branch", value: defaultBranch }),
      );
    }

    const body = {
      branch_id: defaultBranch?.branch_id || 1,
      insurance_company_id: "",
      frm_dte: moment(getToday()).format("DD-MM-YYYY"),
      to_dte: moment(getToday()).format("DD-MM-YYYY"),
      pat_det: "",
      elg_sts: "",
      start_point: 0,
      limit: totalRows,
    };

    await fetchEligibilityList(body);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCheckListFilter();
    }
  };

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handlePerRowsChange = useCallback((newPerPage) => {
    setRowsPerPage(newPerPage);
    setPage(0);
  }, []);

  return (
    <Form
      noValidate
      onSubmit={handleSubmit(handleCheckListFilter)}
      onKeyDown={handleKeyDown}
      className="p-0"
    >
      <FalconComponentCard
        className="shadow-md"
        style={{ position: "relative" }}
      >
        <FalconComponentCard.Header
          className="d-flex fixed-header justify-content-between align-items-center"
          sx={{
            position: "sticky !important",
            top: 68,
            zIndex: 1020,
            backgroundColor: "white",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <Col xs="auto m-0">
            <h5 className="text-primary mb-0 fs-10">Eligibility Check List</h5>
          </Col>
          <Col xs="auto" className="d-flex gap-2">
            <Button size="sm" variant="outline-danger" onClick={handleReset}>
              Reset
            </Button>
            <Button type="submit" size="sm" variant="success">
              Filter
            </Button>
            {!!eligibilitySetting?.export && (
              <Button
                size="sm"
                variant="warning"
                onClick={handleDownloadExcel}
                className="d-flex align-items-center gap-1"
              >
                <FiDownloadCloud size={16} />
                Excel
              </Button>
            )}
          </Col>
        </FalconComponentCard.Header>

        <FalconComponentCard.Body
          style={{
            marginTop: "40px",
            overflowY: "auto",
            maxHeight: "calc(100vh - 200px)",
          }}
        >
          <EligibilityCheckListFilter
            watch={watch}
            control={control}
            setValue={setValue}
            resetTrigger={resetTrigger}
          />

          <EligibilityCheckListTable
            totalRows={totalRows}
            handlePageChange={handlePageChange}
            handlePerRowsChange={handlePerRowsChange}
            data={eligibilityCheckList}
            page={page}
            rowsPerPage={rowsPerPage}
          />
        </FalconComponentCard.Body>
      </FalconComponentCard>
    </Form>
  );
};

export default EligibilityCheckList;
